// src/components/Popups/ExamPermissionGrantPopup.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchStudents } from "../../store/studentsSlice";
import { grantExamPermissionBulk, getMe } from "../../api/examService";
import StudentItem from "../Common/StudentItem";
import AlertPills from "../Utils/AlertPills";

const TZ_MD = "Europe/Chisinau";
const TAB_STUDENTS = "students";
const TAB_SELECTED = "selected";
const INITIAL_VISIBLE_COUNT = 20;
const LOAD_MORE_STEP = 10;
const EXAM_PERMISSION_HOURS = 12;

const firstLetter = (value) =>
   String(value || "")
      .trim()
      .charAt(0) || "";

function getInitials(student) {
   const fn = String(student?.firstName || "").trim();
   const ln = String(student?.lastName || "").trim();

   const a = firstLetter(fn);
   const b = firstLetter(ln);
   if (a && b) return (a + b).toUpperCase();

   const two = fn.slice(0, 2);
   if (two) return two.toUpperCase();

   return "–";
}

function hashStringToUInt(str) {
   let h = 0;
   for (let i = 0; i < str.length; i++) {
      h = (h * 31 + str.charCodeAt(i)) | 0;
   }
   return h >>> 0;
}

const AVATAR_HUES = [
   { h: 70, s: 75 },
   { h: 0, s: 100 },
   { h: 30, s: 100 },
   { h: 54, s: 95 },
   { h: 130, s: 65 },
   { h: 210, s: 90 },
   { h: 255, s: 98 },
   { h: 285, s: 100 },
   { h: 330, s: 96 },
];
const AVATAR_LIGHTNESSES = [94, 92, 90, 88, 86, 84, 82, 80, 78, 76, 74];
const AVATAR_COLORS = AVATAR_HUES.flatMap(({ h, s }) =>
   AVATAR_LIGHTNESSES.map((l) => `hsl(${h} ${s}% ${l}%)`),
);

function getAvatarColorFromName(student) {
   const fullName =
      `${student?.firstName || ""} ${student?.lastName || ""}`.trim();
   const hasLetter = /\p{L}/u.test(fullName);
   if (!hasLetter) return AVATAR_COLORS[0];

   const normalized = fullName.normalize("NFKD");
   const idx = hashStringToUInt(normalized) % AVATAR_COLORS.length;
   return AVATAR_COLORS[idx];
}

function fmtMd(iso) {
   if (!iso) return "-";
   return new Date(iso).toLocaleString("ro-RO", {
      timeZone: TZ_MD,
      dateStyle: "medium",
      timeStyle: "short",
   });
}

function _partsForTZ(date, timeZone) {
   const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
   });
   const parts = fmt.formatToParts(date);
   const get = (type) =>
      Number(parts.find((part) => part.type === type)?.value);
   return {
      y: get("year"),
      m: get("month"),
      d: get("day"),
      hh: get("hour"),
      mm: get("minute"),
      ss: get("second"),
   };
}

function nowInChisinauAsDate() {
   const { y, m, d, hh, mm, ss } = _partsForTZ(new Date(), TZ_MD);
   return new Date(Date.UTC(y, m - 1, d, hh, mm, ss, 0));
}

function isoFromChisinauNowPlusMinutes(minutes = 60) {
   const base = nowInChisinauAsDate();
   return new Date(base.getTime() + Number(minutes || 0) * 60000).toISOString();
}

function buildSearchHaystack(record) {
   return [
      record?.firstName,
      record?.lastName,
      record?.fullName,
      record?.phone,
      record?.email,
   ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
}

function matchesQuery(record, query) {
   const normalized = String(query || "")
      .trim()
      .toLowerCase();
   if (!normalized) return true;
   return buildSearchHaystack(record).includes(normalized);
}

export default function ExamPermissionGrantPopup() {
   const dispatch = useDispatch();
   const studentsAll = useSelector((state) => state.students?.list || []);

   const [meId, setMeId] = useState(null);
   const [q, setQ] = useState("");
   const [activeTab, setActiveTab] = useState(TAB_STUDENTS);
   const [selected, setSelected] = useState(() => new Set());
   const [visibleByTab, setVisibleByTab] = useState(() => ({
      [TAB_STUDENTS]: INITIAL_VISIBLE_COUNT,
      [TAB_SELECTED]: INITIAL_VISIBLE_COUNT,
   }));
   const [alerts, setAlerts] = useState([]);
   const [busy, setBusy] = useState(false);

   const MAX_ATTEMPTS = 1;

   useEffect(() => {
      if (!studentsAll?.length) dispatch(fetchStudents());
   }, [dispatch]); // eslint-disable-line

   useEffect(() => {
      let alive = true;

      (async () => {
         try {
            const me = await getMe();
            const id = Number(me?.id);
            if (alive && Number.isInteger(id) && id > 0) {
               setMeId(id);
            }
         } catch (_) {
            // fallback: serviciul va arunca eroare dacă lipsește actorul valid
         }
      })();

      return () => {
         alive = false;
      };
   }, []);

   const hasUserRole = (user) => {
      const role = String(user?.role ?? user?.Role ?? user?.userRole ?? "")
         .trim()
         .toUpperCase();
      if (role === "USER") return true;

      const roles = Array.isArray(user?.roles)
         ? user.roles.map((item) => String(item).toUpperCase())
         : [];
      return roles.includes("USER");
   };

   const students = useMemo(
      () => (studentsAll || []).filter(hasUserRole),
      [studentsAll],
   );

   const studentColorById = useMemo(() => {
      const map = new Map();
      for (const student of students) {
         map.set(Number(student.id), getAvatarColorFromName(student));
      }
      return map;
   }, [students]);

   const namesById = useMemo(() => {
      const map = new Map();
      for (const student of studentsAll || []) {
         map.set(
            Number(student.id),
            `${student.firstName || ""} ${student.lastName || ""}`.trim() ||
               `#${student.id}`,
         );
      }
      return map;
   }, [studentsAll]);

   const filteredStudents = useMemo(
      () => students.filter((student) => matchesQuery(student, q)),
      [q, students],
   );

   const selectedCount = selected.size;

   const selectedStudents = useMemo(
      () =>
         students.filter(
            (student) =>
               selected.has(Number(student.id)) && matchesQuery(student, q),
         ),
      [q, selected, students],
   );

   const pushAlert = (type, text) =>
      setAlerts((prev) => [
         ...prev,
         { id: Date.now() + Math.random(), type, text },
      ]);

   const popAlert = () => setAlerts((prev) => prev.slice(0, -1));

   const toggleOne = useCallback((id) => {
      setSelected((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });
   }, []);

   const handleLoadMore = useCallback((tabKey, totalCount) => {
      setVisibleByTab((prev) => ({
         ...prev,
         [tabKey]: Math.min(
            (prev?.[tabKey] ?? INITIAL_VISIBLE_COUNT) + LOAD_MORE_STEP,
            totalCount,
         ),
      }));
   }, []);

   async function handleGrant() {
      if (selected.size === 0) {
         pushAlert("error", "Selectează cel puțin un elev.");
         return;
      }

      if (!Number.isInteger(meId) || meId <= 0) {
         pushAlert(
            "error",
            "Nu am putut determina instructorId pentru utilizatorul curent.",
         );
         return;
      }

      try {
         setBusy(true);

         const validUntilIsoRaw = isoFromChisinauNowPlusMinutes(
            EXAM_PERMISSION_HOURS * 60
         );
         const grantedAtIso = nowInChisinauAsDate().toISOString();

         const res = await grantExamPermissionBulk({
            userIds: Array.from(selected).map(Number),
            validUntil: validUntilIsoRaw,
            maxAttempts: MAX_ATTEMPTS,
            grantedById: meId,
         });

         const successIds = Array.isArray(res?.successIds)
            ? res.successIds.map(Number)
            : [];
         const failedArr = Array.isArray(res?.failed) ? res.failed : [];

         setSelected(new Set());
         setActiveTab(TAB_STUDENTS);

         if (successIds.length > 0) {
            const names = successIds.map((id) => namesById.get(id) || `#${id}`);
            const shown = names.slice(0, 5).join(", ");
            const extra = names.length > 5 ? ` +${names.length - 5} alți` : "";

            pushAlert(
               "info",
               `Permisiune ACORDATĂ pentru ${
                  names.length
               } elevi: ${shown}${extra}.
Încercări: ${MAX_ATTEMPTS}. Valabil până la: ${fmtMd(validUntilIsoRaw)}.
Acordat la: ${fmtMd(grantedAtIso)}.`,
            );
         }

         if (failedArr.length > 0) {
            for (const failure of failedArr) {
               const userId = Number(failure?.userId);
               const name = namesById.get(userId) || `#${userId}`;
               const message = failure?.error || "Eroare necunoscută.";
               pushAlert(
                  "error",
                  `Nu s-a putut acorda permisiunea pentru ${name} (${userId}): ${message}`,
               );
            }
         }
      } catch (error) {
         pushAlert(
            "error",
            error?.message || "Eroare la acordarea permisiunii.",
         );
      } finally {
         setBusy(false);
      }
   }

   const visibleStudents = useMemo(
      () =>
         filteredStudents.slice(
            0,
            visibleByTab[TAB_STUDENTS] || INITIAL_VISIBLE_COUNT,
         ),
      [filteredStudents, visibleByTab],
   );

   const visibleSelectedStudents = useMemo(
      () =>
         selectedStudents.slice(
            0,
            visibleByTab[TAB_SELECTED] || INITIAL_VISIBLE_COUNT,
         ),
      [selectedStudents, visibleByTab],
   );

   const tabs = [
      { id: TAB_STUDENTS, label: "Studenți", count: students.length },
      { id: TAB_SELECTED, label: "Selectați", count: selectedCount },
   ];

   const renderSelectControl = (student) => {
      const id = Number(student?.id);
      const checked = selected.has(id);

      return (
         <label
            className={`examPermissionPopup__selector${
               checked ? " is-selected" : ""
            }`}
            onClick={(event) => event.stopPropagation()}
         >
            <input
               type="checkbox"
               checked={checked}
               onChange={() => toggleOne(id)}
               className="examPermissionPopup__checkbox"
               aria-label={`Selectează ${student?.firstName || ""} ${
                  student?.lastName || ""
               }`}
            />
         </label>
      );
   };

   const renderStudentRow = (student) => {
      const id = Number(student?.id);
      const color = studentColorById.get(id) || AVATAR_COLORS[0];

      return (
         <StudentItem
            key={id}
            student={student}
            color={color}
            initials={getInitials(student)}
            onOpen={() => toggleOne(id)}
            highlightQuery={q}
            secondaryText={student?.phone || student?.email || "–"}
            showChevron={false}
            endAdornment={renderSelectControl(student)}
            className={`examPermissionPopup__studentItem${
               selected.has(id) ? " is-selected" : ""
            }`}
            highlightClassName="examPermissionPopup__highlight"
         />
      );
   };

   const renderTabContent = () => {
      if (activeTab === TAB_SELECTED) {
         if (!selectedStudents.length) {
            return (
               <div className="examPermissionPopup__empty">
                  Nu ai elevi selectați. Selectează-i din tabul „Studenți”.
               </div>
            );
         }

         return (
            <div className="examPermissionPopup__items">
               {visibleSelectedStudents.map(renderStudentRow)}
               <div className="examPermissionPopup__listFooter">
                  <span className="examPermissionPopup__counter">
                     Afișate {visibleSelectedStudents.length} din{" "}
                     {selectedStudents.length}
                  </span>
                  {visibleSelectedStudents.length < selectedStudents.length && (
                     <button
                        type="button"
                        className="examPermissionPopup__button examPermissionPopup__button--secondary examPermissionPopup__loadMore"
                        onClick={() =>
                           handleLoadMore(TAB_SELECTED, selectedStudents.length)
                        }
                     >
                        Afișează încă{" "}
                        {Math.min(
                           LOAD_MORE_STEP,
                           selectedStudents.length -
                              visibleSelectedStudents.length,
                        )}
                     </button>
                  )}
               </div>
            </div>
         );
      }

      if (!filteredStudents.length) {
         return (
            <div className="examPermissionPopup__empty">
               Nu am găsit elevi pentru căutarea introdusă.
            </div>
         );
      }

      return (
         <div className="examPermissionPopup__items">
            {visibleStudents.map(renderStudentRow)}
            <div className="examPermissionPopup__listFooter">
               <span className="examPermissionPopup__counter">
                  Afișate {visibleStudents.length} din {filteredStudents.length}
               </span>
               {visibleStudents.length < filteredStudents.length && (
                  <button
                     type="button"
                     className="examPermissionPopup__button examPermissionPopup__button--secondary examPermissionPopup__loadMore"
                     onClick={() =>
                        handleLoadMore(TAB_STUDENTS, filteredStudents.length)
                     }
                  >
                     Afișează încă{" "}
                     {Math.min(
                        LOAD_MORE_STEP,
                        filteredStudents.length - visibleStudents.length,
                     )}
                  </button>
               )}
            </div>
         </div>
      );
   };

   return (
      <>
         <section className="examPermissionPopup">
            <div className="examPermissionPopup__header">
               <h3 className="examPermissionPopup__title">Acces examen </h3>
            </div>

            <div className="examPermissionPopup__tabsBar">
               <div
                  className="examPermissionPopup__tabs"
                  role="tablist"
                  aria-label="Taburi popup permisiune examen"
               >
                  {tabs.map((tab) => (
                     <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        className={`examPermissionPopup__tab${
                           activeTab === tab.id ? " is-active" : ""
                        }`}
                        onClick={() => setActiveTab(tab.id)}
                     >
                        <span>{tab.label}</span>
                        <strong>{tab.count}</strong>
                     </button>
                  ))}
               </div>

               <button
                  type="button"
                  className="examPermissionPopup__button examPermissionPopup__button--primary examPermissionPopup__grantButton"
                  onClick={handleGrant}
                  disabled={selectedCount === 0 || busy}
                  title={
                     selectedCount === 0
                        ? "Selectează cel puțin un elev"
                        : undefined
                  }
               >
                  {busy ? "Se acordă…" : "Acordă permisiunea"}
               </button>
            </div>

            <div className="examPermissionPopup__toolbar">
               <input
                  type="text"
                  className="examPermissionPopup__search"
                  placeholder="Caută după nume, telefon sau e-mail…"
                  value={q}
                  onChange={(event) => setQ(event.target.value)}
               />
            </div>

            <div className="examPermissionPopup__listWrapper" role="tabpanel">
               {renderTabContent()}
            </div>
         </section>

         <AlertPills messages={alerts} onDismiss={popAlert} />
      </>
   );
}
